export type HuddleCameraQuality = 'auto' | '720p' | '1080p' | '2160p';
export type HuddleCameraFrameRate = 'auto' | 15 | 30 | 60;
export const HUDDLE_CAMERA_NUMERIC_CONTROLS = ['zoom', 'focusDistance', 'exposureCompensation', 'colorTemperature', 'brightness', 'contrast', 'saturation', 'sharpness'] as const;
export const HUDDLE_CAMERA_MODE_CONTROLS = ['focusMode', 'exposureMode', 'whiteBalanceMode'] as const;
export type HuddleCameraNumericControl = typeof HUDDLE_CAMERA_NUMERIC_CONTROLS[number];
export type HuddleCameraModeControl = typeof HUDDLE_CAMERA_MODE_CONTROLS[number];
export type HuddleCameraEffect = 'faceFraming';
export interface HuddleCameraRange { min: number; max: number; step?: number }
export interface HuddleCameraPreferences {
  quality?: HuddleCameraQuality;
  frameRate?: HuddleCameraFrameRate;
  mirrorPreview?: boolean;
  controls?: Partial<Record<HuddleCameraNumericControl, number> & Record<HuddleCameraModeControl, string>>;
  effects?: Partial<Record<HuddleCameraEffect, boolean>>;
}
export interface HuddleCameraCapabilities {
  width?: HuddleCameraRange;
  height?: HuddleCameraRange;
  frameRate?: HuddleCameraRange;
  numeric: Partial<Record<HuddleCameraNumericControl, HuddleCameraRange>>;
  modes: Partial<Record<HuddleCameraModeControl, string[]>>;
  effects?: Partial<Record<HuddleCameraEffect, boolean[]>>;
}
export interface HuddleCameraSettings {
  width?: number;
  height?: number;
  frameRate?: number;
  numeric: Partial<Record<HuddleCameraNumericControl, number>>;
  modes: Partial<Record<HuddleCameraModeControl, string>>;
  effects?: Partial<Record<HuddleCameraEffect, boolean>>;
}
export interface HuddleCameraDetails {
  capabilities: HuddleCameraCapabilities;
  settings: HuddleCameraSettings;
  preferences: HuddleCameraPreferences;
  applying: boolean;
  error?: string;
}

const QUALITY_DIMENSIONS = { '720p': [1280, 720], '1080p': [1920, 1080], '2160p': [3840, 2160] } as const;
const MODES = new Set(['none', 'manual', 'single-shot', 'continuous']);
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const record = (value: unknown): Record<string, unknown> => typeof value === 'object' && value !== null ? value as Record<string, unknown> : {};
const cameraEnded = (track: MediaStreamTrack): boolean => track.readyState === 'ended';

function range(value: unknown, lower: number, upper: number, variable = false): HuddleCameraRange | undefined {
  const source = record(value);
  if (!finite(source.min) || !finite(source.max) || source.min < lower || source.max > upper || source.max < source.min || (variable && source.min === source.max)) return undefined;
  const result: HuddleCameraRange = { min: source.min, max: source.max };
  if (finite(source.step) && source.step > 0 && source.step <= source.max - source.min) result.step = source.step;
  return result;
}
const NUMERIC_BOUNDS: Record<HuddleCameraNumericControl, [number, number]> = {
  zoom: [0.01, 1000], focusDistance: [0, 100000], exposureCompensation: [-100, 100], colorTemperature: [1, 100000],
  brightness: [-1000000, 1000000], contrast: [-1000000, 1000000], saturation: [-1000000, 1000000], sharpness: [-1000000, 1000000],
};

/** Only expose capabilities reported for this permitted track, never browser-wide feature guesses. */
export function normalizeHuddleCameraCapabilities(value: unknown): HuddleCameraCapabilities {
  const source = record(value);
  const capabilities: HuddleCameraCapabilities = {
    width: range(source.width, 1, 16384), height: range(source.height, 1, 16384), frameRate: range(source.frameRate, 0, 240), numeric: {}, modes: {},
  };
  for (const key of HUDDLE_CAMERA_NUMERIC_CONTROLS) {
    const control = range(source[key], ...NUMERIC_BOUNDS[key], true);
    if (control) capabilities.numeric[key] = control;
  }
  for (const key of HUDDLE_CAMERA_MODE_CONTROLS) {
    const values = source[key];
    if (!Array.isArray(values)) continue;
    const supported = [...new Set(values.filter((mode): mode is string => typeof mode === 'string' && MODES.has(mode)))];
    // A single value describes a fixed mode and must not be mistaken for unavailable support.
    if (supported.length > 0) capabilities.modes[key] = supported;
  }
  if (Array.isArray(source.faceFraming)) {
    const values = [...new Set(source.faceFraming.filter((value): value is boolean => typeof value === 'boolean'))];
    if (values.length) capabilities.effects = { faceFraming: values };
  }
  return capabilities;
}

export function huddleCameraSupportsQuality(capabilities: HuddleCameraCapabilities, quality: HuddleCameraQuality): boolean {
  if (quality === 'auto') return true;
  const [width, height] = QUALITY_DIMENSIONS[quality];
  return Boolean(capabilities.width && capabilities.height && width >= capabilities.width.min && width <= capabilities.width.max && height >= capabilities.height.min && height <= capabilities.height.max);
}

export function normalizeHuddleCameraPreferences(preferences: HuddleCameraPreferences = {}, capabilities?: HuddleCameraCapabilities): HuddleCameraPreferences {
  const quality = ['720p', '1080p', '2160p'].includes(preferences.quality ?? '') ? preferences.quality! : 'auto';
  const frameRate = preferences.frameRate === 15 || preferences.frameRate === 30 || preferences.frameRate === 60 ? preferences.frameRate : 'auto';
  const normalized: HuddleCameraPreferences = {
    quality: !capabilities || huddleCameraSupportsQuality(capabilities, quality) ? quality : 'auto',
    frameRate: !capabilities || frameRate === 'auto' || (capabilities.frameRate && frameRate >= capabilities.frameRate.min && frameRate <= capabilities.frameRate.max) ? frameRate : 'auto',
    mirrorPreview: preferences.mirrorPreview !== false, controls: {},
  };
  // Keep explicit Off as intent even when another camera cannot expose that control.
  if (typeof preferences.effects?.faceFraming === 'boolean') normalized.effects = { faceFraming: preferences.effects.faceFraming };
  if (!capabilities) return normalized;
  for (const key of HUDDLE_CAMERA_NUMERIC_CONTROLS) {
    const bounds = capabilities.numeric[key]; const value = preferences.controls?.[key];
    if (!bounds || !finite(value)) continue;
    let bounded = Math.max(bounds.min, Math.min(bounds.max, value));
    if (bounds.step) bounded = Math.max(bounds.min, Math.min(bounds.max, bounds.min + Math.round((bounded - bounds.min) / bounds.step) * bounds.step));
    normalized.controls![key] = Number(bounded.toPrecision(12));
  }
  for (const key of HUDDLE_CAMERA_MODE_CONTROLS) {
    const mode = preferences.controls?.[key];
    if (mode && capabilities.modes[key]?.includes(mode)) normalized.controls![key] = mode;
  }
  return normalized;
}

type NativeCameraConstraints = Partial<Record<HuddleCameraModeControl, ConstrainDOMString> & Record<HuddleCameraEffect, ConstrainBoolean>>;

/** Defaults are requested only after the permitted device reports support. */
function automaticCameraConstraints(preferences: HuddleCameraPreferences, capabilities?: HuddleCameraCapabilities): NativeCameraConstraints {
  const automatic: NativeCameraConstraints = {};
  if (preferences.controls?.focusMode === undefined && preferences.controls?.focusDistance === undefined && capabilities?.modes.focusMode?.includes('continuous')) automatic.focusMode = { ideal: 'continuous' };
  if (preferences.effects?.faceFraming === undefined && capabilities?.effects?.faceFraming?.includes(true)) automatic.faceFraming = { ideal: true };
  return automatic;
}

/** Ideals allow camera negotiation; callers display getSettings(), not the requested preset. */
export function huddleCameraConstraints(preferences: HuddleCameraPreferences = {}, capabilities?: HuddleCameraCapabilities): MediaTrackConstraints {
  const selected = normalizeHuddleCameraPreferences(preferences, capabilities);
  const [width, height] = QUALITY_DIMENSIONS[selected.quality === 'auto' || !selected.quality ? '720p' : selected.quality];
  const constraints: MediaTrackConstraints & Partial<Record<HuddleCameraNumericControl, ConstrainDouble>> & NativeCameraConstraints = {
    width: { ideal: width }, height: { ideal: height }, frameRate: { ideal: selected.frameRate === 'auto' || !selected.frameRate ? 30 : selected.frameRate },
    ...automaticCameraConstraints(preferences, capabilities),
  };
  for (const key of HUDDLE_CAMERA_NUMERIC_CONTROLS) {
    const value = selected.controls?.[key]; if (value !== undefined) constraints[key] = { exact: value };
  }
  for (const key of HUDDLE_CAMERA_MODE_CONTROLS) {
    const value = selected.controls?.[key]; if (value !== undefined) constraints[key] = { exact: value };
  }
  const framing = selected.effects?.faceFraming;
  if (framing !== undefined && capabilities?.effects?.faceFraming?.includes(framing)) constraints.faceFraming = { exact: framing };
  return constraints;
}

export function readHuddleCameraDetails(track: MediaStreamTrack, preferences: HuddleCameraPreferences = {}): HuddleCameraDetails {
  let rawCapabilities: unknown; let rawSettings: unknown;
  try { rawCapabilities = track.getCapabilities?.(); } catch { /* Some browsers cannot inspect the selected device. */ }
  try { rawSettings = track.getSettings?.(); } catch { /* Keep unavailable settings blank. */ }
  const capabilities = normalizeHuddleCameraCapabilities(rawCapabilities); const source = record(rawSettings);
  const settings: HuddleCameraSettings = { numeric: {}, modes: {} };
  for (const key of ['width', 'height', 'frameRate'] as const) if (finite(source[key]) && source[key] > 0 && source[key] <= (key === 'frameRate' ? 240 : 16384)) settings[key] = source[key];
  for (const key of HUDDLE_CAMERA_NUMERIC_CONTROLS) {
    const value = source[key]; const bounds = capabilities.numeric[key];
    if (bounds && finite(value) && value >= bounds.min && value <= bounds.max) settings.numeric[key] = value;
  }
  for (const key of HUDDLE_CAMERA_MODE_CONTROLS) {
    const value = source[key]; if (typeof value === 'string' && MODES.has(value)) settings.modes[key] = value;
  }
  if (typeof source.faceFraming === 'boolean') settings.effects = { faceFraming: source.faceFraming };
  return { capabilities, settings, preferences: normalizeHuddleCameraPreferences(preferences, capabilities), applying: false };
}

export async function applyHuddleCameraPreferences(track: MediaStreamTrack, preferences: HuddleCameraPreferences): Promise<HuddleCameraDetails> {
  const before = readHuddleCameraDetails(track, preferences);
  if (cameraEnded(track)) throw new DOMException('Camera unavailable', 'InvalidStateError');
  if (typeof track.applyConstraints !== 'function') throw new DOMException('Camera settings unavailable', 'NotSupportedError');
  const constraints = huddleCameraConstraints(preferences, before.capabilities);
  try {
    await track.applyConstraints(constraints);
  } catch (error) {
    const automatic = automaticCameraConstraints(preferences, before.capabilities);
    if (cameraEnded(track) || !Object.keys(automatic).length) throw error;
    // Optional native defaults must not prevent an explicit format or hardware adjustment.
    const fallback: MediaTrackConstraints & NativeCameraConstraints = { ...constraints };
    for (const key of Object.keys(automatic) as (keyof NativeCameraConstraints)[]) delete fallback[key];
    await track.applyConstraints(fallback);
  }
  return readHuddleCameraDetails(track, before.preferences);
}

/** Initialise supported native effects without replacing the OS/device-processed track. */
export async function configureHuddleCamera(track: MediaStreamTrack, preferences: HuddleCameraPreferences = {}): Promise<HuddleCameraDetails> {
  const details = readHuddleCameraDetails(track, preferences);
  const supportedEffects = typeof preferences.effects?.faceFraming === 'boolean' && details.capabilities.effects?.faceFraming?.includes(preferences.effects.faceFraming);
  const explicit = Object.keys(details.preferences.controls ?? {}).length > 0 || supportedEffects;
  const automatic = Object.keys(automaticCameraConstraints(preferences, details.capabilities)).length > 0;
  if ((!explicit && !automatic) || typeof track.applyConstraints !== 'function') return details;
  try {
    return await applyHuddleCameraPreferences(track, preferences);
  } catch (error) {
    if (explicit || cameraEnded(track)) throw error;
    // A platform may advertise an effect without permitting the page to change it.
    return readHuddleCameraDetails(track, preferences);
  }
}
